/* eslint-disable @typescript-eslint/no-unused-vars */
import { request } from "graphql-request";

// Your deployed subgraph URLs
const CAMP_SUBGRAPH_URL =
  "https://api.goldsky.com/api/public/project_clu8sr03ji34301z2b4xte1g5/subgraphs/bridge-camp/v1.0.1/gn";
const ETHEREUM_SUBGRAPH_URL =
  "https://api.goldsky.com/api/public/project_clu8sr03ji34301z2b4xte1g5/subgraphs/bridge-ethereum/v1.0.1/gn";
const STAKING_SUBGRAPH_URL =
  "https://api.goldsky.com/api/public/project_clu8sr03ji34301z2b4xte1g5/subgraphs/staking-camp/v1.0.1/gn";

export interface BridgeTransaction {
  id: string;
  guid?: string;
  status:
    | "PENDING_HOLD"
    | "READY_TO_RELEASE"
    | "BRIDGING"
    | "COMPLETED"
    | "INITIATED"
    | "FAILED";
  sourceChain: "camp" | "ethereum";
  destinationChain: "camp" | "ethereum";
  amount: string;
  timestamp: string;
  transactionHash: string;
  unlockTimestamp?: string;
  fromAddress?: string;
  toAddress?: string;
  correlatedWith?: string;
  correlationMethod?: "guid" | "user-amount-time";
  // Additional fields from subgraph
  user?: string;
  completedAt?: string;
  depositedAt?: string;
  srcEid?: string;
  dstEid?: string;
  depositId?: string;
  // Enhanced property for ready to execute transactions
  isReadyToExecute?: boolean;
  // Token identification
  tokenSymbol?: string;
  tokenAddress?: string;
  tokenType?: "CAMP" | "ETH" | "USDC";
}

export interface CrossChainTransaction {
  id: string;
  guid: string;
  status: "INITIATED" | "SENT" | "COMPLETED" | "RECEIVED";
  sourceChain: string;
  destinationChain: string;
  amount: string;
  sender: string;
  recipient: string;
  initiatedAt: string;
  completedAt?: string;
  depositId?: string;
  sourceTransaction?: BridgeTransaction;
  destinationTransaction?: BridgeTransaction;
}

export interface UserTransactionsResponse {
  pendingHold: BridgeTransaction[];
  bridging: BridgeTransaction[];
  completed: BridgeTransaction[];
  total: number;
}

// Staking Subgraph Interfaces
export interface StakingVaultData {
  id: string;
  totalSupply: string;
  totalStakers: string;
  currentAPR: string;
  currentAPY: string;
  totalRewardsPaid: string;
  rewardRate: string;
  periodFinish: string;
  rewardsDuration: string;
  totalStakeTransactions: string;
  totalWithdrawTransactions: string;
  totalClaimTransactions: string;
}

export interface StakingUserData {
  id: string;
  address: string;
  totalStaked: string;
  totalClaimed: string;
  totalWithdrawn: string;
  currentStake: string;
  lastStakeTimestamp: string;
  stakes: StakingTransaction[];
  claims: StakingTransaction[];
  withdrawals: StakingTransaction[];
}

export interface StakingTransaction {
  id: string;
  amount: string;
  timestamp: string;
  blockNumber: string;
  transactionHash: string;
  type: "stake" | "claim" | "withdraw";
  user?: {
    id: string;
  };
  vaultTotalSupply?: string;
  rewardRate?: string;
  vault?: {
    id: string;
    vaultAddress: string;
    stakingToken: string;
  };
}

export interface StakingDashboardData {
  stakingVault: StakingVaultData | null;
  recentActivity: StakingTransaction[];
  topStakers: Array<{
    id: string;
    currentStake: string;
    totalStaked: string;
  }>;
}

/**
 * Define status priority for deduplication
 * Higher number = higher priority (later in lifecycle)
 */
const STATUS_PRIORITY = {
  PENDING_HOLD: 1,
  READY_TO_RELEASE: 2,
  INITIATED: 2, // Ethereum side equivalent
  BRIDGING: 3,
  COMPLETED: 4,
  FAILED: 5,
} as const;

/**
 * Create a unique key for grouping transactions that represent the same logical bridge operation
 */
function getTransactionGroupKey(tx: BridgeTransaction): string {
  // Prefer depositId if available (most reliable for correlation)
  if (tx.depositId) {
    return `deposit-${tx.depositId}`;
  }

  // Fall back to GUID if available
  if (tx.guid) {
    return `guid-${tx.guid}`;
  }

  // Enhanced fallback: user + amount + timestamp (same timestamp indicates same transaction)
  // For cross-chain transactions, use exact timestamp match first
  const user = tx.user || tx.fromAddress;
  const exactKey = `exact-${user}-${tx.amount}-${tx.timestamp}`;

  // Also try a time window approach (30-minute windows) for slight timing differences
  const timeWindow = Math.floor(parseInt(tx.timestamp) / 1800) * 1800; // 30-minute windows
  const windowKey = `window-${user}-${tx.amount}-${timeWindow}`;

  // Return exact match key - we'll handle both in deduplication
  return exactKey;
}

/**
 * Enhanced grouping that tries multiple correlation methods
 */
function getTransactionGroupKeys(tx: BridgeTransaction): string[] {
  const keys: string[] = [];

  // Primary keys (most reliable)
  // Only use depositId if it's not "0" (which appears to be a default/null value)
  if (tx.depositId && tx.depositId !== "0") {
    keys.push(`deposit-${tx.depositId}`);
  }

  if (tx.guid) {
    keys.push(`guid-${tx.guid}`);
  }

  // Fallback keys
  const user = tx.user || tx.fromAddress;

  // Exact timestamp match
  keys.push(`exact-${user}-${tx.amount}-${tx.timestamp}`);

  // Remove window-based correlation as it's causing too much transitive grouping
  // The GUID correlation should be sufficient for proper deduplication

  // Hash-based correlation (for same tx appearing on different chains)
  if (tx.transactionHash) {
    keys.push(`hash-${tx.transactionHash}`);
  }

  return keys;
}

/**
 * Enhanced deduplication logic that groups transactions by logical bridge operation
 * and keeps only the highest status for each group
 */
function deduplicateTransactions(
  allTransactions: BridgeTransaction[],
): BridgeTransaction[] {
  // First pass: group by all possible keys
  const keyToTransactions = new Map<string, BridgeTransaction[]>();
  const transactionToKeys = new Map<string, string[]>();

  allTransactions.forEach((tx) => {
    const txId = tx.id;
    const keys = getTransactionGroupKeys(tx);
    transactionToKeys.set(txId, keys);

    keys.forEach((key) => {
      if (!keyToTransactions.has(key)) {
        keyToTransactions.set(key, []);
      }
      keyToTransactions.get(key)!.push(tx);
    });
  });

  // Second pass: merge overlapping groups using Union-Find
  const transactionGroups = new Map<string, BridgeTransaction[]>();
  const processedTransactions = new Set<string>();

  allTransactions.forEach((tx) => {
    if (processedTransactions.has(tx.id)) return;

    // Find all transactions that should be grouped with this one
    const group: BridgeTransaction[] = [tx];
    const groupKeys = new Set(transactionToKeys.get(tx.id) || []);

    // Expand group by finding transactions that share any key
    let foundNew = true;
    while (foundNew) {
      foundNew = false;

      Array.from(groupKeys).forEach((key) => {
        const relatedTxs = keyToTransactions.get(key) || [];
        relatedTxs.forEach((relatedTx) => {
          if (
            !processedTransactions.has(relatedTx.id) &&
            !group.find((t) => t.id === relatedTx.id)
          ) {
            group.push(relatedTx);
            foundNew = true;

            // Add all keys from this related transaction
            const relatedKeys = transactionToKeys.get(relatedTx.id) || [];
            relatedKeys.forEach((k) => groupKeys.add(k));
          }
        });
      });
    }

    // Mark all transactions in this group as processed
    group.forEach((t) => processedTransactions.add(t.id));

    // Create a group identifier
    const groupId = `group-${Math.min(
      ...group.map((t) => parseInt(t.timestamp)),
    )}-${group[0].id}`;
    transactionGroups.set(groupId, group);
  });

  // For each group, keep only the transaction with the highest status priority
  const deduplicatedTransactions: BridgeTransaction[] = [];

  transactionGroups.forEach((txGroup, groupKey) => {
    if (txGroup.length === 1) {
      // Single transaction, no deduplication needed
      deduplicatedTransactions.push(txGroup[0]);
    } else {
      // Multiple transactions - pick the one with highest status priority
      const sortedByPriority = txGroup.sort((a, b) => {
        const priorityA =
          STATUS_PRIORITY[a.status as keyof typeof STATUS_PRIORITY] || 0;
        const priorityB =
          STATUS_PRIORITY[b.status as keyof typeof STATUS_PRIORITY] || 0;

        // If priorities are equal, prefer newer timestamp
        if (priorityA === priorityB) {
          return parseInt(b.timestamp) - parseInt(a.timestamp);
        }

        return priorityB - priorityA;
      });

      const selectedTx = sortedByPriority[0];
      deduplicatedTransactions.push(selectedTx);
    }
  });

  // Final verification: check if any duplicates still exist by amount + user
  const amountUserKeys = deduplicatedTransactions.map(
    (tx) => `${tx.amount}-${tx.user || tx.fromAddress}`,
  );
  const uniqueAmountUserKeys = new Set(amountUserKeys);

  return deduplicatedTransactions;
}

export async function getUserTransactions(
  userAddress: string,
): Promise<UserTransactionsResponse> {
  if (!userAddress) {
    return { pendingHold: [], bridging: [], completed: [], total: 0 };
  }

  const checksumAddress = userAddress.toLowerCase();

  try {
    // Fetch ALL transactions for the user (not separated by status)
    const [campData, ethereumData] = (await Promise.all([
      request(
        CAMP_SUBGRAPH_URL,
        `
        query GetAllCampTransactions($userAddress: String!) {
          bridgeTransactions(
            where: { user: $userAddress }
            orderBy: timestamp, orderDirection: desc
            first: 100
          ) {
            id amount unlockTimestamp timestamp depositId guid
            fromAddress toAddress transactionHash status sourceChain destinationChain user
            completedAt depositedAt srcEid dstEid nonce
            tokenSymbol tokenAddress tokenType
          }
        }`,
        { userAddress: checksumAddress },
      ),

      request(
        ETHEREUM_SUBGRAPH_URL,
        `
        query GetAllEthereumTransactions($userAddress: String!) {
          bridgeTransactions(
            where: { user: $userAddress }
            orderBy: timestamp, orderDirection: desc
            first: 100
          ) {
            id amount timestamp guid depositId
            fromAddress toAddress transactionHash status sourceChain destinationChain user
            srcEid dstEid nonce
            tokenSymbol tokenAddress tokenType
          }
        }`,
        { userAddress: checksumAddress },
      ),
    ])) as [
      { bridgeTransactions: BridgeTransaction[] },
      { bridgeTransactions: BridgeTransaction[] },
    ];

    // Combine all transactions
    const allTransactions: BridgeTransaction[] = [
      ...(campData.bridgeTransactions || []),
      ...(ethereumData.bridgeTransactions || []),
    ];

    // Apply deduplication logic
    const deduplicatedTransactions = deduplicateTransactions(allTransactions);

    // Group by final status after deduplication
    const pendingHold = deduplicatedTransactions.filter(
      (tx) => tx.status === "PENDING_HOLD",
    );

    // For READY_TO_RELEASE, check if they're actually ready to execute
    const readyToRelease = deduplicatedTransactions.filter(
      (tx) => tx.status === "READY_TO_RELEASE",
    );

    const bridging = deduplicatedTransactions.filter(
      (tx) => tx.status === "BRIDGING" || tx.status === "INITIATED",
    );

    const completed = deduplicatedTransactions.filter(
      (tx) => tx.status === "COMPLETED",
    );

    // For READY_TO_RELEASE transactions, check if unlock time has passed
    // If yes, they should be shown as "Ready to Execute" in the UI
    const enhancedReadyToRelease = readyToRelease.map((tx) => {
      if (tx.unlockTimestamp) {
        const unlockTime = parseInt(tx.unlockTimestamp) * 1000;
        const now = Date.now();
        return {
          ...tx,
          isReadyToExecute: now >= unlockTime,
        };
      }
      return { ...tx, isReadyToExecute: false };
    });

    // Sort by timestamp (newest first)
    const sortByTimestamp = (a: BridgeTransaction, b: BridgeTransaction) =>
      parseInt(b.timestamp) - parseInt(a.timestamp);

    const result = {
      // Combine PENDING_HOLD and READY_TO_RELEASE as they're both "pending" states
      pendingHold: [...pendingHold, ...enhancedReadyToRelease].sort(
        sortByTimestamp,
      ),
      bridging: bridging.sort(sortByTimestamp),
      completed: completed.sort(sortByTimestamp),
      total: deduplicatedTransactions.length,
    };

    return result;
  } catch (error) {
    return { pendingHold: [], bridging: [], completed: [], total: 0 };
  }
}

// Rest of your functions remain the same...
export async function getUserBridgeTransactions(userAddress: string) {
  return getUserTransactions(userAddress);
}

export function formatAmount(amount: string, decimals: number = 18): string {
  const divisor = Math.pow(10, decimals);
  return (parseInt(amount) / divisor).toFixed(6);
}

export function formatTimestamp(timestamp: string): Date {
  return new Date(parseInt(timestamp) * 1000);
}

export function shortenHash(hash: string): string {
  return `${hash.slice(0, 6)}...${hash.slice(-4)}`;
}

export function getBridgeDirection(
  transaction: BridgeTransaction,
): "camp-to-eth" | "eth-to-camp" {
  if (transaction.sourceChain && transaction.destinationChain) {
    if (
      transaction.sourceChain === "camp" &&
      transaction.destinationChain === "ethereum"
    ) {
      return "camp-to-eth";
    }
    if (
      transaction.sourceChain === "ethereum" &&
      transaction.destinationChain === "camp"
    ) {
      return "eth-to-camp";
    }
  }

  if (transaction.status === "PENDING_HOLD") {
    return "camp-to-eth";
  }

  if (transaction.sourceChain === "camp") {
    return "camp-to-eth";
  }
  return "eth-to-camp";
}

export function getExplorerUrl(
  txHash: string,
  direction: "camp-to-eth" | "eth-to-camp",
  status?: string,
): string {
  // For non-completed transactions, txHash is from origin chain
  // For completed transactions, txHash is from destination chain
  const isCompleted = status === "completed" || status === "COMPLETED";

  if (direction === "camp-to-eth") {
    if (isCompleted) {
      // Completed: destination is Ethereum Mainnet
      return `https://etherscan.io/tx/${txHash}`;
    } else {
      // Non-completed: origin is Camp Network
      return `https://camp.cloud.blockscout.com/tx/${txHash}`;
    }
  } else {
    if (isCompleted) {
      // Completed: destination is Camp Network
      return `https://camp.cloud.blockscout.com/tx/${txHash}`;
    } else {
      // Non-completed: origin is Ethereum Mainnet
      return `https://etherscan.io/tx/${txHash}`;
    }
  }
}

export async function testCorrelation(
  userAddress: string = "0x9d10e0ddf27961d2904ff5fc25d28f7d1d3ad41a",
) {
  try {
    const result = await getUserTransactions(userAddress);

    // Check for potential duplicates by amount and user
    const amounts = [
      ...result.pendingHold,
      ...result.bridging,
      ...result.completed,
    ].map((tx) => tx.amount);
    const uniqueAmounts = new Set(amounts);
    const hasPotentialDuplicates = amounts.length !== uniqueAmounts.size;

    return result;
  } catch (error) {
    return null;
  }
}

export async function testPendingHoldTransactions(
  userAddress: string = "0x9d10e0ddf27961d2904ff5fc25d28f7d1d3ad41a",
) {
  try {
    const result = await getUserTransactions(userAddress);

    const readyTransactions = result.pendingHold.filter((tx) => {
      if (tx.unlockTimestamp) {
        const unlockTime = new Date(parseInt(tx.unlockTimestamp) * 1000);
        const now = new Date();
        return now.getTime() >= unlockTime.getTime();
      }
      return false;
    });

    return {
      total: result.pendingHold.length,
      ready: readyTransactions.length,
      readyTransactions,
    };
  } catch (error) {
    return null;
  }
}

// Staking Subgraph Functions
export async function getStakingVaultData(
  vaultId?: string,
): Promise<StakingVaultData | null> {
  try {
    // First try to see if subgraph is reachable with a simple query
    const testData = await request<{ _meta: { block: { number: string } } }>(
      STAKING_SUBGRAPH_URL,
      `
      query TestConnection {
        _meta {
          block {
            number
          }
        }
      }
      `,
    );

    // Use provided vaultId or default to CAMP vault
    const vaultAddress =
      vaultId || "0xA3b517fD67a21af560a754aA86DAC6c29B3b5aFf";

    const data = await request<{ stakingVault: StakingVaultData | null }>(
      STAKING_SUBGRAPH_URL,
      `
      query VaultOverview($vaultId: String!) {
        stakingVault(id: $vaultId) {
          id
          totalSupply
          totalStakers
          currentAPR
          currentAPY
          totalRewardsPaid
          rewardRate
          periodFinish
          rewardsDuration
          totalStakeTransactions
          totalWithdrawTransactions
          totalClaimTransactions
        }
      }
      `,
      { vaultId: vaultAddress.toLowerCase() },
    );

    if (!data.stakingVault) {
      console.warn("No staking vault data found in subgraph for", vaultAddress);
      // Try with original case from contract
      const dataWithOriginalCase = await request<{
        stakingVault: StakingVaultData | null;
      }>(
        STAKING_SUBGRAPH_URL,
        `
        query VaultOverview($vaultId: String!) {
          stakingVault(id: $vaultId) {
            id
            totalSupply
            totalStakers
            currentAPR
            currentAPY
            totalRewardsPaid
            rewardRate
            periodFinish
            rewardsDuration
            totalStakeTransactions
            totalWithdrawTransactions
            totalClaimTransactions
          }
        }
        `,
        { vaultId: vaultAddress },
      );
      return dataWithOriginalCase.stakingVault || null;
    }

    return data.stakingVault;
  } catch (error) {
    console.error("Failed to fetch vault data:", error);
    return null;
  }
}

export async function getStakingUserData(
  userAddress: string,
): Promise<StakingUserData | null> {
  if (!userAddress) return null;

  try {
    const checksumAddress = userAddress.toLowerCase();

    const data = await request<{ user: StakingUserData | null }>(
      STAKING_SUBGRAPH_URL,
      `
      query UserProfile($userAddress: String!) {
        user(id: $userAddress) {
          id
          address
          totalStaked
          totalClaimed
          totalWithdrawn
          currentStake
          lastStakeTimestamp
          stakes(orderBy: timestamp, orderDirection: desc, first: 20) {
            id
            amount
            timestamp
            blockNumber
            transactionHash
            vaultTotalSupply
            rewardRate
          }
          claims(orderBy: timestamp, orderDirection: desc, first: 20) {
            id
            amount
            timestamp
            blockNumber
            transactionHash
          }
          withdrawals(orderBy: timestamp, orderDirection: desc, first: 20) {
            id
            amount
            timestamp
            blockNumber
            transactionHash
            vaultTotalSupply
          }
        }
      }
      `,
      { userAddress: checksumAddress },
    );

    return data.user || null;
  } catch (error) {
    console.error("Failed to fetch user staking data:", error);
    return null;
  }
}

interface StakingDashboardResponse {
  stakingVault: StakingVaultData | null;
  recentActivity: StakingTransaction[];
  topStakers: Array<{
    id: string;
    currentStake: string;
    totalStaked: string;
  }>;
}

export async function getStakingDashboard(): Promise<StakingDashboardData> {
  try {
    const data = await request<StakingDashboardResponse>(
      STAKING_SUBGRAPH_URL,
      `
      query StakingDashboard {
        stakingVault(id: "0xA3b517fD67a21af560a754aA86DAC6c29B3b5aFf") {
          id
          totalSupply
          totalStakers
          currentAPR
          currentAPY
          rewardRate
          periodFinish
          rewardsDuration
          totalRewardsPaid
        }

        recentActivity: transactions(
          orderBy: timestamp
          orderDirection: desc
          first: 10
        ) {
          id
          type
          amount
          timestamp
          transactionHash
          blockNumber
          user {
            id
          }
        }

        topStakers: users(
          orderBy: currentStake
          orderDirection: desc
          first: 5
          where: { currentStake_gt: "0" }
        ) {
          id
          currentStake
          totalStaked
        }
      }
      `,
    );

    return {
      stakingVault: data.stakingVault || null,
      recentActivity: data.recentActivity || [],
      topStakers: data.topStakers || [],
    };
  } catch (error) {
    console.error("Failed to fetch staking dashboard data:", error);
    return {
      stakingVault: null,
      recentActivity: [],
      topStakers: [],
    };
  }
}

interface UserActivityResponse {
  stakes: Omit<StakingTransaction, "type">[];
  claims: Omit<StakingTransaction, "type">[];
  withdrawals: Omit<StakingTransaction, "type">[];
}

export async function getUserStakingActivity(
  userAddress: string,
  vaultId?: string,
): Promise<StakingTransaction[]> {
  if (!userAddress) return [];

  try {
    const checksumAddress = userAddress.toLowerCase();

    // Build where clause - filter by user and optionally by vault
    const whereClause = vaultId
      ? `{ user: $userAddress, vault: $vaultId }`
      : `{ user: $userAddress }`;

    const variables: Record<string, string> = { userAddress: checksumAddress };
    if (vaultId) {
      variables.vaultId = vaultId.toLowerCase();
    }

    const data = await request<UserActivityResponse>(
      STAKING_SUBGRAPH_URL,
      `
      query UserActivity($userAddress: String!${
        vaultId ? ", $vaultId: String!" : ""
      }) {
        stakes: userStakes(
          where: ${whereClause}
          orderBy: timestamp
          orderDirection: desc
          first: 50
        ) {
          id
          amount
          timestamp
          blockNumber
          transactionHash
          vaultTotalSupply
          rewardRate
          vault {
            id
            vaultAddress
            stakingToken
          }
        }

        claims: userClaims(
          where: ${whereClause}
          orderBy: timestamp
          orderDirection: desc
          first: 50
        ) {
          id
          amount
          timestamp
          blockNumber
          transactionHash
          vault {
            id
            vaultAddress
            stakingToken
          }
        }

        withdrawals: userWithdrawals(
          where: ${whereClause}
          orderBy: timestamp
          orderDirection: desc
          first: 50
        ) {
          id
          amount
          timestamp
          blockNumber
          transactionHash
          vaultTotalSupply
          vault {
            id
            vaultAddress
            stakingToken
          }
        }
      }
      `,
      variables,
    );

    // Combine all transactions with type annotation and vault info
    const allTransactions: StakingTransaction[] = [
      ...(data.stakes || []).map((tx) => ({ ...tx, type: "stake" as const })),
      ...(data.claims || []).map((tx) => ({ ...tx, type: "claim" as const })),
      ...(data.withdrawals || []).map((tx) => ({
        ...tx,
        type: "withdraw" as const,
      })),
    ];

    // Sort by timestamp (newest first)
    return allTransactions.sort(
      (a, b) => parseInt(b.timestamp) - parseInt(a.timestamp),
    );
  } catch (error) {
    console.error("Failed to fetch user staking activity:", error);
    return [];
  }
}
